import type { InferType } from "yup";
import { EmploymentVerificationCriteriaSchemaV1 } from "./types/employment-verification-criteria-schema-v1";
import type { AdjudicationStatus } from "../../utils/renderStatusTag";
import type {
  CodeOption,
  Code,
  SubCategory,
  Category,
} from "./types/canadian-criminal-record-types";
import type {
  AdjudicationFormValues,
  BasicCanadianCriminalRecordCriteria,
} from "./types/form-values";
import {
  BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES,
  EMPLOYMENT_VERIFICATION_CHECK_TYPES,
} from "./constants";

type Ruleset = InferType<
  typeof EmploymentVerificationCriteriaSchemaV1
>["ruleset"];

// ============================================================================
// Employment Verification Utils
// ============================================================================

export const getMismatchScore = (ruleset: Partial<Ruleset> | undefined) => {
  const mismatchOutcome = ruleset?.mismatch_count;

  if (!mismatchOutcome) return "NOT_SET";
  if (mismatchOutcome?.one_instance && mismatchOutcome?.two_or_more_instances) {
    return mismatchOutcome?.one_instance ===
      mismatchOutcome?.two_or_more_instances
      ? mismatchOutcome.one_instance
      : "CUSTOM";
  }
  return mismatchOutcome.one_instance || mismatchOutcome.two_or_more_instances;
};

export const getDateVarianceScore = (ruleset: Partial<Ruleset> | undefined) => {
  const dateVariance = ruleset?.date_variance;

  if (!dateVariance) return "NOT_SET";

  const lessThanNinety = dateVariance.ninety_days_or_less;
  const greaterThanNinety = dateVariance.over_ninety_days;

  if (!lessThanNinety && !greaterThanNinety) return "NOT_SET";
  if (lessThanNinety && greaterThanNinety) {
    return lessThanNinety === greaterThanNinety ? lessThanNinety : "CUSTOM";
  }
  return lessThanNinety || greaterThanNinety;
};

export const getUnverifiedEmploymentScore = (
  ruleset: Partial<Ruleset> | undefined
) => {
  const unverifiedEmployment = ruleset?.unverified_employment_count;

  if (!unverifiedEmployment) return "NOT_SET";

  const oneUnverified = unverifiedEmployment.one_instance;
  const twoOrMore = unverifiedEmployment.two_or_more_instances;

  if (!oneUnverified && !twoOrMore) return "NOT_SET";
  if (oneUnverified && twoOrMore) {
    return oneUnverified === twoOrMore ? oneUnverified : "CUSTOM";
  }
  return oneUnverified || twoOrMore;
};

// ============================================================================
// Canadian Criminal Record Utils
// ============================================================================

export interface CriteriaAndCategoryResult {
  criteriaIndex: number;
  categoryIndex: number;
  currentRuleset: Category[];
  categoryData: Category | null;
}

/**
 * Gets criteria and category data for Canadian Criminal Record checks
 */
export function getCriteriaAndCategory(
  values: AdjudicationFormValues,
  subCategory: SubCategory
): CriteriaAndCategoryResult {
  const criteria = values?.criteria?.find(
    (criteria): criteria is BasicCanadianCriminalRecordCriteria =>
      BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(criteria.check_type)
  );

  if (!criteria) {
    return {
      criteriaIndex: -1,
      categoryIndex: -1,
      currentRuleset: [],
      categoryData: null,
    };
  }

  const criteriaIndex = values.criteria.findIndex((c) =>
    BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(c.check_type)
  );
  const currentRuleset = criteria.ruleset || [];
  const categoryData = currentRuleset.find(
    (item) => item.category === subCategory.value
  );

  const categoryIndex = currentRuleset.findIndex(
    (item) => item.category === subCategory.value
  );

  return {
    criteriaIndex,
    categoryIndex,
    currentRuleset,
    categoryData: categoryData || null,
  };
}

/**
 * Gets existing code data from criteria
 */
export function getExistingCodeData(
  values: AdjudicationFormValues,
  subCategory: SubCategory,
  code: CodeOption
): Code | null {
  const { categoryData } = getCriteriaAndCategory(values, subCategory);

  if (!categoryData) return null;

  const existingCode = categoryData.codes?.find((c) => c.code === code.key);

  return existingCode || null;
}

/**
 * Determines the score/status for a code based on its saved data
 */
export function getCodeScore(
  values: AdjudicationFormValues,
  subCategory: SubCategory,
  code: CodeOption
): AdjudicationStatus {
  const existingCodeData = getExistingCodeData(values, subCategory, code);

  if (!existingCodeData) return "NOT_SET";

  // If global outcome is set, return that
  if (
    existingCodeData.global_outcome &&
    existingCodeData.global_outcome !== "NOT_SET"
  ) {
    return existingCodeData.global_outcome as AdjudicationStatus;
  }

  // Check if any conditional values are set
  const conditionalValues = [
    existingCodeData.one_offense?.zero_to_three_years_since_offense_outcome,
    existingCodeData.one_offense?.three_to_ten_years_since_offense_outcome,
    existingCodeData.one_offense?.ten_or_more_years_since_offense_outcome,
    existingCodeData.two_or_more_offenses
      ?.zero_to_three_years_since_offense_outcome,
    existingCodeData.two_or_more_offenses
      ?.three_to_ten_years_since_offense_outcome,
    existingCodeData.two_or_more_offenses
      ?.ten_or_more_years_since_offense_outcome,
  ];

  const hasConditionalValues = conditionalValues.some(
    (value) => value && value !== "NOT_SET"
  );

  // If conditional values are set, return CUSTOM
  if (hasConditionalValues) {
    return "CUSTOM";
  }

  return "NOT_SET";
}

export function getCheckTypeName(
  checkType: string,
  t: (key: string) => string
): string {
  switch (checkType) {
    case "EMPLOYMENT_VERIFICATIONS_1":
      return t("Employment Verification");
    case "EMPLOYMENT_VERIFICATION_MANUAL_1":
      return t("Employment Verification (Manual)");
    case "EMPLOYMENT_VERIFICATION_MANUAL_US_1":
      return t("Employment Verification (Manual US)");
    case "BASIC_CANADIAN_CRIMINAL_RECORD_REPORT_1":
      return t("Canadian Criminal Record Check: Basic");
    default:
      return checkType;
  }
}
export function isChildEmploymentVerification(checkType: string): boolean {
  return (
    [
      "EMPLOYMENT_VERIFICATION_MANUAL_1",
      "EMPLOYMENT_VERIFICATION_MANUAL_US_1",
    ] satisfies typeof EMPLOYMENT_VERIFICATION_CHECK_TYPES
  ).includes(checkType);
}

export function isParentEmploymentVerification(checkType: string): boolean {
  return (
    [
      "EMPLOYMENT_VERIFICATIONS_1",
    ] satisfies typeof EMPLOYMENT_VERIFICATION_CHECK_TYPES
  ).includes(checkType);
}
